/**
 * The player's own screen. Portrait-first, because it is a phone.
 *
 * The table is **behind** this, not elsewhere: TableView in its backdrop
 * variant fills the screen and the hand sits over the bottom of it. Before that
 * a player saw only their own cards and had to look up at a shared screen to
 * know what was happening, which meant a game with no television in the room
 * was a game you played half blind.
 *
 * The trick is drawn at full card size for exactly that reason - see the note
 * in TableView. What lives here is what you *hold* and what you may *do*.
 *
 * Legal moves come from the server. This file highlights them and sends the one
 * you tapped; it never decides whether a card may be played.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isSolo } from '../net/match.js';
import type { ZoneView } from '../net/match.js';
import type { MatchView } from '../net/match.js';
import type { GameLayoutWire, VariantSpecWire, ZoneSpecWire } from '../net/session.js';
import { Zone } from '../render/Zone.js';
import {
  boardColumns,
  choiceLabel,
  movesByCard,
  movesByZone,
  playableCards,
  resolve,
} from '../render/layout.js';
import { seatsOffered } from '../render/seat-actions.js';
import { faceOf } from '../render/cards.js';
import { Card } from '../render/Card.js';
import { useArrival } from '../render/use-arrival.js';
import { useCounterEvent } from '../render/use-counter-event.js';
import { WardrobeSheet } from './Wardrobe.js';
import { RoomStatus } from './HostControls.js';
import { TableMenu } from './TableMenu.js';
import { TableTopBar } from './TableTopBar.js';
import { ChatBar } from './ChatBar.js';
import { Icon } from '../render/Icon.js';
import type { IconName } from '../render/Icon.js';
import { Lobby } from './Lobby.js';
import { playCardSound, playRoundOver, playYourTurn } from '../render/sound.js';
import { Clock, useTurnWarning } from '../render/Clock.js';
import { describeMatchError } from './errors.js';
import { Shuffling } from './Shuffling.js';
import { TableView } from './TableView.js';
import { useRetreat } from './use-retreat.js';
import { ObserverCount } from './Observers.js';
import { Prompts, promptMoves } from './Prompt.js';
import { CardLegend } from './CardLegend.js';
import { Rules } from './Rules.js';
import { ChatSheet } from './Chat.js';
import { VoiceButtons } from './Voice.js';
import type { VoiceControls } from './Voice.js';
import type { ChatEntry } from '../net/match.js';
import { ThrownCards, throwFor, useTurned } from './ThrownCard.js';
import { Standings } from './Standings.js';
import type { Throw } from './ThrownCard.js';
import { useSession } from '../session-context.js';
import { SEAT_END } from '@cards/toolkit';

/* A stable empty hand, so the arrival hook is not handed a new array to
   compare against on every render. */
const NO_CARDS: readonly string[] = [];

/**
 * "drawCard" -> "Draw card".
 *
 * Move names are the game's identifiers, not copy, and they reach the client
 * unchanged so the renderer stays generic. Splitting camelCase is the most a
 * client can do without knowing what game it is showing; a game that wants
 * better wording should say so in its layout, which is a gap worth noting
 * rather than papering over here.
 */
function humanise(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * What a move's button says: the game's word for it, or the split name.
 *
 * The gap the note above used to end on. Splitting the identifier is still the
 * default and still right for nearly everything; `moveLabels` is how a game
 * says the word its players actually use, which for a patience is "turn"
 * rather than "draw" - there is no hand here to draw into.
 */
function labelFor(layout: GameLayoutWire, move: string): string {
  return layout.moveLabels?.[move] ?? humanise(move);
}

/**
 * The mark for a move, where the platform has one.
 *
 * Move names are the game's to choose - `drawCard`, `bid`, `layMeld`,
 * `callCheat` - so this cannot be a mark per move without inventing a visual
 * language per game, and inventing one badly for the next game that ships.
 *
 * It is a **convention** instead, the same kind as `targetScore` and
 * `handNumber` in the top bar: a move whose name begins with one of these
 * gets the mark, and every other move simply has none and says less. A game
 * that wants the deck mark names its move `draw…`, which is what all three
 * that deal already do.
 */
const MOVE_MARKS: ReadonlyArray<readonly [string, IconName]> = [
  // drawCard, drawStock, drawDiscard.
  ['draw', 'pair-of-cards'],
  // Handing the turn on rather than doing anything with it.
  ['pass', 'chevron-right'],
  // Out of the hand, and out of the round.
  ['fold', 'close'],
];

function moveMark(move: string): IconName | null {
  const name = move.toLowerCase();
  for (const [prefix, icon] of MOVE_MARKS) {
    if (name.startsWith(prefix)) return icon;
  }
  return null;
}

/** A tapped card with more than one move behind it needs a follow-up question. */
/**
 * Why your hand just changed, when nothing on screen can say it (a platform
 * convention, like `passesLeft` below).
 *
 * A game publishes `notice` in the viewer's OWN view - never the table's - as
 * `{ n, text }`. The counter is what makes a repeat land: the same sentence
 * twice is two events, and a client watching the words alone would show the
 * first and miss the second.
 *
 * Held for six seconds and then gone. It explains something that has just
 * happened, and an explanation that stays on screen stops being about the
 * thing that prompted it.
 *
 * Absent for every game that publishes none, which is all of them but one.
 */
function HandNotice({ view }: { view: MatchView }) {
  const notice = view.view['notice'] as { n?: number; text?: string } | null | undefined;
  const [shown, setShown] = useState<string | null>(null);

  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  /*
   * `useCounterEvent` and not a comparison of the text: its rules are exactly
   * the ones wanted here, and the first of them is the one that matters most -
   * a first value is a baseline, so somebody reconnecting is not told about
   * two cards they drew ten minutes ago.
   */
  const latest = useRef(notice);
  latest.current = notice;
  useCounterEvent(notice?.n ?? 0, () => {
    const text = latest.current?.text;
    if (text === undefined) return;
    setShown(text);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShown(null), 6000);
  });

  if (shown === null) return null;
  /* `role="status"`: it is worth speaking, and worth speaking without
     interrupting whatever is being read. */
  return (
    <p className="hand-notice" role="status">
      {shown}
    </p>
  );
}

/**
 * The stock's remaining life, where a game counts it.
 *
 * Absent for every game that does not publish the number, which is most of
 * them - a trick game's stock is dealt out and never turned.
 */
function PassesLeft({ view }: { view: MatchView }) {
  const left = view.view['passesLeft'];
  if (typeof left !== 'number' || left < 0) return null;

  return (
    <span
      className={`passes-left${left <= 1 ? ' last' : ''}`}
      title="How many more times you may turn the stock over"
    >
      {left === 0 ? 'No passes left' : left === 1 ? 'Last pass' : `${String(left)} passes left`}
    </span>
  );
}

function MoveChoice({
  options,
  zones,
  labels,
  onPick,
  onCancel,
}: {
  options: Array<{ move: string; args: string[] }>;
  /** The table as it stands, so a place argument can say which card is there. */
  zones: Record<string, ZoneView> | undefined;
  /** What the layout calls each zone, for a destination holding no card yet. */
  labels: ReadonlyMap<string, string>;
  onPick: (option: { move: string; args: string[] }) => void;
  onCancel: () => void;
}) {
  return (
    <div className="sheet" role="dialog" aria-label="Choose">
      <div className="sheet-body">
        <p className="sheet-title">Choose</p>
        <div className="sheet-options">
          {options.map((option) => (
            <button
              key={`${option.move}:${option.args.join(',')}`}
              type="button"
              className="big-button"
              onClick={() => onPick(option)}
            >
              {/* The extra arguments ARE the choice: a suit, a pile, a
                  partner. See `choiceLabel` for what each kind reads as - and
                  for why an index is dropped rather than shown. */}
              {(() => {
                const parts = option.args
                  .slice(1)
                  .map((arg) => ({ arg, said: choiceLabel(arg, zones, labels) }))
                  .filter((part) => part.said !== null);

                if (parts.length === 0) return humanise(option.move);
                return parts.map((part) => (
                  <span key={part.arg} className={part.said?.red ? 'arg red' : 'arg'}>
                    {part.said?.text}
                  </span>
                ));
              })()}
            </button>
          ))}
        </div>
        <button type="button" className="text-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Where a pyramid zone's cards sit, if this game has one.
 *
 * A platform convention like `trickPlays`: a fixed name with a fixed shape,
 * read straight out of the view. Absent for every game that is not a patience,
 * and the renderer then draws nothing positional.
 */
function pyramidSlots(view: MatchView): number[] | undefined {
  const raw = view.view['slots'];
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((value): value is number => typeof value === 'number');
}

export function Hand({
  view,
  layout,
  zones,
  roomCode,
  gameName,
  objective,
  howToPlay,
  variantSpecs,
  onMove,
  onHostAction,
  onLeave,
  onSay,
  onType,
  onWear,
  onLookAgain,
  chatLog,
  allowTextChat,
  voice,
  turnLimits,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  zones: ZoneSpecWire[];
  roomCode: string;
  gameName: string;
  /** One line on the point of the game, and the rules, both sent with the join. */
  objective: string;
  howToPlay: readonly string[];
  variantSpecs: VariantSpecWire[];
  onMove: (move: string, args: string[]) => void;
  onHostAction: (action: Record<string, unknown>) => void;
  /** Walking away on purpose. Room owns where that goes. */
  onLeave: () => void;
  onSay: (phraseId: string) => void;
  onType: (text: string) => void;
  /** Wear one of the faces this table lends (D92). */
  onWear: (avatarId: string) => void;
  /** The host changed the room's deck or table; the match must re-read it. */
  onLookAgain: () => void;
  chatLog: ChatEntry[];
  allowTextChat: boolean;
  /** Absent where the deployment has no STUN, so no button is offered at all. */
  voice: VoiceControls | null;
  turnLimits?: { min: number; default: number; max: number } | undefined;
}) {
  const [pending, setPending] = useState<Array<{ move: string; args: string[] }> | null>(null);
  /*
   * Leaving asks first (H21).
   *
   * The seat is HELD when somebody goes, so the table does not get its chair
   * back - it gets a player who is auto-played until the grace period runs out.
   * That is the right behaviour for a dead battery and too much to trigger with
   * one mis-tap, which is why this matches the host's End Game confirm rather
   * than being a bare button.
   */
  const [showRules, setShowRules] = useState(false);
  /* The host's cards-and-table sheet. Opened FROM the menu and in place of it,
     so what is behind it is the table rather than a list of settings. */
  const [showDress, setShowDress] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [throws, setThrows] = useState<Throw[]>([]);
  const screenRef = useRef<HTMLDivElement | null>(null);
  const throwKey = useRef(0);

  /*
   * The card turned from one pile onto another (a patience).
   *
   * Separate state from `throws` above because it is driven by the opposite
   * end of the round trip: a played card is known when you tap it, a turned
   * one only when the server says what it was. They share the one layer.
   */
  const turned = useTurned(
    layout,
    view.lastMove,
    view.view.zones,
    useCallback(() => screenRef.current, []),
  );
  const { config } = useSession();

  const seatCount = view.seats.length || 1;
  /*
   * Questions are not cards.
   *
   * A bid of "3" would otherwise join the playable set, match no card in the
   * hand, and vanish - so they are split off before anything looks at args[0]
   * expecting a card id.
   */
  const asks = promptMoves(layout);
  const cardMoves = view.legalMoves.filter((move) => !asks.has(move.move));
  /*
   * Both given the zones, so a move that names a PLACE rather than a card -
   * Golf's column, Pyramid's slot - hangs off the card sitting there. See
   * `targetOf`. Every game that names cards is unaffected.
   */
  /*
   * Whether this game's cards are laid OUT rather than held.
   *
   * "Nothing on the shared screen" is the honest test, and the three
   * patiences are the games that say it: there is no table to be the other
   * half of a hand, so the hand is the whole surface and lays out as a board.
   * It was keyed off a column-shaped zone before, which was a guess that
   * happened to be right.
   */
  const board = layout.table.length === 0;

  const playable = playableCards(cardMoves, view.view.zones);
  const byCard = movesByCard(cardMoves, view.view.zones);

  /*
   * What tapping a PILE does. See `movesByZone`.
   *
   * The table used to be a picture: every draw went through a button because
   * nothing drawn on the felt could be pressed. Nobody at a real table says
   * "draw card" - they reach for the stock - and in a patience every pile on
   * the board is something you press. The button stays, because it is the one
   * that says what it does.
   */
  const byZone = movesByZone(cardMoves, layout.motions);

  /* What each zone is called, for a chooser naming a destination that has no
     card on it yet. Both halves of the layout, because either can be one. */
  const zoneLabels = new Map<string, string>();
  for (const placement of [...layout.table, ...layout.hand]) {
    if (placement.label !== undefined) zoneLabels.set(placement.zone, placement.label);
  }
  const myTurn = view.seat !== null && view.activeSeats.indexOf(view.seat) >= 0;

  /*
   * Nothing in this screen has anybody on the other end of it in a patience.
   *
   * Voice, typing and the watcher count are all answers to "who else is
   * here", and the answer is nobody - so they are not switched off, they are
   * absent. It also settles what "your turn" is worth: see below.
   */
  const solo = isSolo(view);
  // Only *your* clock running out is worth shouting about.
  const warning = useTurnWarning(view.turn) && myTurn && !view.paused;

  /*
   * The hand gets out of the way when it is not your turn.
   *
   * Called before any early return, because hooks must be.
   */
  const retreat = useRetreat(myTurn, view.lifecycle === 'playing');

  const myHand = view.seat === null ? undefined : view.view.zones?.[`hand:${String(view.seat)}`];
  const handCount = myHand?.cards?.length ?? myHand?.count ?? 0;

  /*
   * The card you just drew, held up before it joins the rest (H2).
   *
   * `travelsToSeat` asks the layout rather than looking for a move called
   * `drawCard`: a move that ends at SEAT_END is one that puts a card in
   * somebody's hand, which is the same declaration the table reads to decide
   * what flies across the felt. A game whose deal is not called "draw" still
   * gets this, and a game that declares no motions still gets nothing.
   */
  const motions = layout.motions;
  const travelsToSeat = useCallback(
    (name: string) =>
      (motions ?? []).some((motion) => motion.move === name && motion.to === SEAT_END),
    [motions],
  );
  const arrival = useArrival(myHand?.cards ?? NO_CARDS, view.lastMove, view.seat, travelsToSeat);

  /*
   * Cues, fired on transitions rather than on every render.
   *
   * A phone is often face-down on the table while somebody else thinks, so
   * "it's you" needs to be audible as well as visible — and now that the hand
   * retreats between turns, the sound is often the first thing you get.
   */
  const wasMyTurn = useRef(false);
  useEffect(() => {
    // Never in a patience: it is always your turn, so announcing one is a
    // chime that means "the cards are dealt" and sounds like a summons.
    if (myTurn && !wasMyTurn.current && !solo) playYourTurn();
    wasMyTurn.current = myTurn;
  }, [myTurn, solo]);

  const lastLifecycle = useRef(view.lifecycle);
  useEffect(() => {
    if (view.lifecycle === 'between' && lastLifecycle.current === 'playing') playRoundOver();
    lastLifecycle.current = view.lifecycle;
  }, [view.lifecycle]);

  /*
   * Before the deal this screen has nothing to be: no cards, no turn, no
   * clock. The lobby takes the whole screen rather than sitting as a status
   * line above an empty hand, because what somebody needs here — the code to
   * read out, the QR to point a phone at — wants the room.
   */
  if (view.lifecycle === 'lobby') {
    return (
      <div className="hand-screen">
        {/* Sitting in a lobby is exactly where a room is most likely to be
            waited out, so the same honesty applies here. */}
        {view.gone ? (
          <div className="banner error">
            This room has closed — taking you back to join another.
          </div>
        ) : !view.connected ? (
          <div className="banner warn">Reconnecting…</div>
        ) : null}
        {view.lastError ? (
          <div className="banner error">{describeMatchError(view.lastError)}</div>
        ) : null}
        <Lobby
          view={view}
          roomCode={roomCode}
          gameName={gameName}
          variantSpecs={variantSpecs}
          turnLimits={turnLimits}
          onHostAction={onHostAction}
          onLeave={onLeave}
        />
      </div>
    );
  }

  const send = (move: string, args: string[]) => {
    playCardSound();

    /*
     * Measured BEFORE the move is sent.
     *
     * The card is about to leave the hand, and once the server's view comes
     * back there is nothing left to measure - the element is gone. So the
     * flight is captured from the screen as it stands, then plays out over the
     * top while the real update arrives underneath.
     */
    const flight = throwFor(layout, move, args[0], screenRef.current);
    if (flight) {
      throwKey.current += 1;
      const next: Throw = { key: throwKey.current, ...flight };
      setThrows((current) => [...current, next]);
      window.setTimeout(() => {
        setThrows((current) => current.filter((candidate) => candidate.key !== next.key));
      }, 800);
    }

    onMove(move, args);
  };

  const tap = (card: string) => {
    const options = byCard.get(card) ?? [];
    if (options.length === 0) return;
    if (options.length === 1) {
      const only = options[0] as { move: string; args: string[] };
      send(only.move, only.args);
      return;
    }
    setPending(options);
  };

  /** The same choice, made by pressing the pile rather than a card on it. */
  const tapZone = (zone: string) => {
    const options = byZone.get(zone) ?? [];
    if (options.length === 0) return;
    if (options.length === 1) {
      const only = options[0] as { move: string; args: string[] };
      send(only.move, only.args);
      return;
    }
    setPending(options);
  };

  /** A handler for a pile, or nothing when no move starts there. */
  const zoneTap = (zone: string): (() => void) | undefined =>
    byZone.has(zone) ? () => tapZone(zone) : undefined;

  /*
   * A handler for a button drawn on somebody's chair - see `seatActions`.
   *
   * The table asks per seat and per move; this answers for the moves the server
   * actually offered, so a button cannot appear where a move would be refused.
   */
  const seatActionTap = (move: string, seat: number): (() => void) | undefined => {
    if (!seatsOffered(view.legalMoves, move).has(seat)) return undefined;
    return () => send(move, [String(seat)]);
  };

  /*
   * The same moves, pointed at yourself.
   *
   * Your own chair is not drawn on the table - the hand is where you are - so
   * the one button you press about yourself would be the one with no seat to
   * sit on. It goes in the strip under the cards instead, where your hand is
   * already reaching.
   */
  const ownActions =
    view.seat === null
      ? []
      : (layout.seatActions ?? []).filter((action) =>
          seatsOffered(view.legalMoves, action.move).has(view.seat as number),
        );

  // Moves with no card argument are buttons rather than cards: "draw", "pass".
  const buttonMoves = cardMoves.filter((move) => typeof move.args[0] !== 'string');

  /*
   * Whether YOU are in the voice conversation.
   *
   * Read from the roster every client already has rather than from
   * VoiceButtons' own state: the chat bar below is a sibling, and reaching
   * into a component for the fact would make one of them own the other.
   */
  const inVoice = voice !== null && view.voice[voice.myId] !== undefined;

  /*
   * The bar is here when there is something to type into and no conversation
   * to type over. See ChatBar for why joining is the trigger rather than voice
   * merely being possible.
   */
  const showChatBar = view.seat !== null && view.allowTextChat && !inVoice && !solo;

  /* Whether the turn is this device's. The screen edge says so on its own; this
     is what puts the countdown next to the buttons it applies to. */
  const onMyTurn = view.seat !== null && view.turn?.seat === view.seat;

  /*
   * The phrases get a button of their own only when voice has not taken the
   * slot. With both available voice wins it, because voice is the one you
   * reach for mid-sentence - the phrases are a tap away in the menu either
   * way, and are the lowest common denominator rather than the main event.
   */
  const phrasesInRow = view.seat !== null && !(voice && view.allowVoice);


  return (
    <div
      /*
        The turn border says "you are the one holding everybody up". In a
        patience nobody is held up and the turn never passes, so it would
        pulse from the deal to the last card - a signal that is always on is
        not a signal, it is a frame.
      */
      className={`room-screen${myTurn && !solo ? ' my-turn' : ''}${
        warning ? ' warning' : ''
      }${board ? ' board' : ''}`}
      /*
        Which game this is, for the stylesheet.

        Not read by anything today, and that is the point: until this existed
        there was NOWHERE to put a tweak meant for one game. The only move
        available was editing the shared rule - `.zone-column` is Klondike's
        and Golf's - so the architecture pushed every refinement of one game
        into a change to all of them.

        A game-specific rule goes under `[data-game='klondike']` and cannot
        reach anywhere else. A shared rule stays shared, and changing one is
        then a deliberate statement that the change is for every game.
      */
      data-game={layout.gameId}
      ref={screenRef}
    >
      {/*
        Two rows: the app, then the game (H8, H9).

        This was one header carrying a game line, the player's name, the room
        code and seven controls, and it wrapped on every phone. The room code
        moved into the Table menu (H10), the seven controls became one button,
        and what is left divides cleanly - the wordmark and this turn on top,
        what you are playing and where you stand underneath.
      */}
      <TableTopBar
        view={view}
        layout={layout}
        gameName={gameName}
        onMenu={() => setShowMenu(true)}
        /*
          Only what the table cannot say for itself.
          
          This carried the turn pill and a clock, and both were the same fact
          twice: the active seat's avatar already wears a sweep with the
          seconds on it (H11), so "Bob's turn 87" up here was a second copy of
          a countdown running six inches below it. What is left is the watcher
          count and the states no single avatar can express - see `TurnPill`.
        */
        status={
          <>
            {solo ? null : <ObserverCount view={view} />}
            <TurnPill view={view} />
          </>
        }
      />

      {/*
       * You came to play and the table was full.
       *
       * Reached /play and ended up without a seat, which the server treats as
       * waiting rather than watching - so say so, and say what ends the wait.
       * Without it this screen is a table with no hand and no explanation.
       *
       * The lobby returns above, so getting here at all means a game is on.
       */}
      {view.seat === null ? (
        <div className="banner warn">
          Waiting for a seat — you will be dealt in when one frees up.
        </div>
      ) : null}

      {view.lastError?.code === 'REMOVED' ? (
        // Checked before `closing`, because a removed player is not being told
        // about the room - they are being told about themselves.
        <div className="banner warn">
          The host removed you from this table — taking you back.
        </div>
      ) : view.closing ? (
        <div className="banner warn">
          That is the last game in this room —{' '}
          {view.isHost ? 'taking you to your games.' : 'taking you back to join another.'}
        </div>
      ) : view.lastError ? (
        <div className="banner error">{describeMatchError(view.lastError)}</div>
      ) : view.gone ? (
        // Checked before `connected`, because this IS disconnected - but the
        // honest version, where we have stopped pretending it might come back.
        <div className="banner error">
          This room has closed — taking you back to join another.
        </div>
      ) : !view.connected ? (
        <div className="banner warn">Reconnecting…</div>
      ) : null}

      {/*
       * The table, filling the screen behind everything below.
       *
       * Rotated so this player sits at the bottom, which is what makes the
       * trick readable as "who played what" rather than as a row of cards.
       */}
      <TableView
        view={view}
        layout={layout}
        zones={zones}
        variant="backdrop"
        onZone={zoneTap}
        onSeatAction={seatActionTap}
      />

      <div className={`hand-layer${retreat.retreated ? ' retreated' : ''}`}>
        {/*
         * The handle, and the only thing still on screen once the hand has
         * retreated. It says what it is holding, because "cards" alone is not
         * worth the tap to find out.
         */}
        <div className="hand-handle">
          <button
            type="button"
            className="handle-grip"
            onClick={retreat.toggle}
            aria-expanded={!retreat.retreated}
          >
            <span className="handle-bar" aria-hidden="true" />
            <span className="handle-label">
              {retreat.retreated ? `Your cards (${String(handCount)})` : 'Hide'}
            </span>
          </button>
        </div>

        <Shuffling
          count={typeof view.view['reshuffles'] === 'number' ? view.view['reshuffles'] : 0}
        />

        {/* Ranked, and it includes you - the ring deliberately does not draw
            your own chair, so this is the only place your own score and your
            own bid appear. */}
        <Standings view={view} layout={layout} />

        <RoomStatus view={view} layout={layout} onAction={onHostAction} />

        {/*
          What the crayon means, for a deck that has been written on.

          Above the cards rather than below, because it is read while deciding
          rather than after - and it renders nothing at all for the eleven
          games that have marked no ranks, so it costs them not even a box.
        */}
        <CardLegend marks={layout.cardMarks ?? {}} />

        <Prompts layout={layout} legalMoves={view.legalMoves} seats={view.seats} onMove={send} />

        {/*
          A board is laid out, a hand is stacked.

          `.hand-zones` stacks its zones, which is right for the one or two a
          hand has and hopeless for a patience: Golf's seven columns came out
          full width, one per row, three thousand pixels of scroll in a seven
          hundred pixel window, with one column visible at a time.

          The placements say where they go - see `row` and `col` - and this
          says whether anything is listening. A game with cards on the table
          keeps the strip it has always had.
        */}
        <main
          className={`hand-zones${board ? ' board' : ''}`}
          /* As wide as the game says it is, so a seven-column board is not
             laid out in eight and left sitting half a column off centre. */
          style={board ? ({ '--board-cols': boardColumns(layout.hand) } as React.CSSProperties) : undefined}
        >
          {layout.hand.map((placement) =>
            resolve(placement, zones, view.view.zones, seatCount, view.seat).map((resolved) => (
              <Zone
                key={resolved.id}
                placement={placement}
                zone={resolved.view}
                playable={playable}
                fresh={arrival.fresh}
                arriving={arrival.arriving ?? turned.arriving ?? undefined}
                /* Only ever read for the zone whose top card is being held
                   back, so handing it to every zone costs nothing. */
                beneath={turned.beneath}
                slots={pyramidSlots(view)}
                onCard={tap}
                onZone={zoneTap(placement.zone)}
                underCount={
                  placement.under === undefined
                    ? undefined
                    : (view.view.zones?.[placement.under]?.count ?? 0)
                }
              />
            )),
          )}
        </main>

        {/*
          Where a drawn card lands, and then where it is held up.

          The slot is always here and always empty-looking, because it is a
          MEASUREMENT as much as a place: the table reads it to find out where
          to fly the card to, so the flight ends exactly where the face is
          about to appear rather than somewhere above it. An element that only
          existed while the card was already being held up would be too late
          to aim at.

          The card is not in the fan yet - `arriving` keeps its place there
          without drawing it - so for this second and a bit there is one card
          on the screen, in one place, and it is this one.

          A button because a tap ends it. On a turn where you may play the card
          you just drew, a second of animation sits between a player and their
          move, and anything in that position has to be skippable.
        */}
        <div className="draw-slot" aria-hidden={arrival.reveal ? undefined : true}>
          {arrival.reveal ? (
            <button
              type="button"
              className="draw-reveal"
              onClick={arrival.dismiss}
              aria-live="polite"
              aria-label={`You drew the ${faceOf(arrival.reveal).label}. Tap to put it away.`}
            >
              <Card card={arrival.reveal} />
            </button>
          ) : null}
        </div>

        {/*
          One strip under the hand, in the same place every turn (H13).

          The buttons used to appear only when there were any, so a turn you
          could only play a card on had no row at all and everything below it
          moved up. A control that is sometimes there is one you have to look
          for; this is always in the same place, and says what it has to say.

          It also absorbs the separate "Waiting for the others" line, which was
          a second strip saying the other half of the same thing.

          And it answers H15 without a banner. The plan asked that a turn
          banner justify itself against the chat bar already at the bottom of a
          phone, and it cannot: "play a card or draw" belongs where the
          controls are, not in a bar of its own above them.
        */}
        {/*
          Why cards arrived, for the one person they arrived for.

          Above the strip rather than in it. Inside, it was a third thing
          competing for a 390px row that already holds a clock, two buttons and
          a microphone - it wrapped to three lines and squeezed "Draw card"
          narrower than its own label. A sentence and a row of buttons want
          different amounts of room, so they get different rows.
        */}
        {view.lifecycle === 'playing' ? <HandNotice view={view} /> : null}

        {view.lifecycle === 'playing' ? (
          <div className="hand-actions">
            {/*
              Your own countdown, beside your own buttons.

              You have no avatar on this table - the hand is where "you" are -
              so the sweep that marks every other seat has nowhere to go. Here
              it sits with the thing it is timing.
            */}
            {onMyTurn && view.turn ? <Clock turn={view.turn} audible /> : null}

            {/*
              Your own declaration, first in the row and always there.
              
              First because it is the only control here that matters in the
              second after somebody else's card lands, and always there because
              a button you can press at any time cannot be one that appears -
              which also keeps the left edge of this strip still while Draw and
              Pass come and go beside it.
            */}
            {ownActions.map((action) => (
              <button
                key={action.move}
                type="button"
                className="big-button declare"
                onClick={() => send(action.move, [String(view.seat)])}
              >
                {action.ownLabel}
              </button>
            ))}

            {/*
              How much stock is left to turn over, before it runs out.

              A patience ends when the stock can no longer be turned, and it
              used to end without warning - you recycled once more and the
              screen said the deal was dead. This is the same fact said before
              it matters rather than after.

              `passesLeft` is a platform convention, so this needs to know no
              game: Pyramid spends a budget of them and Klondike's goes back up
              the moment a card gets home, and both mean "how close this is to
              being over".
            */}
            <PassesLeft view={view} />

            {buttonMoves.map((move) => {
              const mark = moveMark(move.move);
              return (
                <button
                  key={move.move}
                  type="button"
                  className="big-button"
                  onClick={() => send(move.move, move.args)}
                >
                  {mark ? <Icon name={mark} /> : null}
                  {labelFor(layout, move.move)}
                </button>
              );
            })}

            {/*
              Voice sits with the game's own buttons, not in the menu.

              Joining a conversation happens at a moment - somebody says "can
              you hear me" - rather than being something you go looking for.
              Beside Draw and Pass is where a hand is already reaching.

              `view.allowVoice` as well as the deployment's capability: the
              server refuses to relay signalling while the host has it off, so
              a button here would be one that cannot work (L3).
            */}
            {voice && view.allowVoice && !solo ? (
              <VoiceButtons view={view} controls={voice} />
            ) : null}

            {/* No voice at this table, so the phrases take the slot it would
                have had - the one thing everybody can always do. */}
            {phrasesInRow ? (
              <button
                type="button"
                className="rules-button"
                onClick={() => setShowChat(true)}
                aria-label="Quick phrases"
              >
                <Icon name="message" />
              </button>
            ) : null}

            {/* Nothing to press, but something to do - the cards are lifted
                and waiting, and a first-time player does not know that yet. */}
            {buttonMoves.length === 0 && playable.size > 0 ? (
              <p className="hand-hint">Tap a card to play it</p>
            ) : null}

            {/*
              Nothing you can do, and why.
              
              "Waiting for the others" at a table for one is a lie with nobody
              to wait for - and it is what a finished patience says while the
              deal it just lost is still on the screen. The honest version
              names the only thing left.
            */}
            {view.legalMoves.length === 0 ? (
              <p className="hand-hint">
                {solo ? 'This deal is over — deal again from the table menu.' : 'Waiting for the others…'}
              </p>
            ) : null}
          </div>
        ) : null}

        {/*
          Under the actions, inside the hand layer so it retreats with the
          cards - a message box left floating over the table while the hand
          slid away would be the one thing on screen not attached to anything.
        */}
        {showChatBar ? (
          <ChatBar onType={onType} onPhrases={() => setShowChat(true)} />
        ) : null}
      </div>

      {pending ? (
        <MoveChoice
          options={pending}
          zones={view.view.zones}
          labels={zoneLabels}
          onPick={(option) => {
            setPending(null);
            send(option.move, option.args);
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      <ThrownCards throws={[...throws, ...turned.throws]} />

      {showChat ? (
        <ChatSheet
          phrases={config?.phrases ?? []}
          log={chatLog}
          seats={view.seats}
          allowTextChat={allowTextChat}
          onSay={onSay}
          onType={onType}
          onClose={() => setShowChat(false)}
        />
      ) : null}

      {showMenu ? (
        <TableMenu
          view={view}
          layout={layout}
          roomCode={roomCode}
          gameName={gameName}
          variantSpecs={variantSpecs}
          phrases={config?.phrases ?? []}
          limits={turnLimits}
          autoRetreat={retreat.auto}
          onAutoRetreat={retreat.setAuto}
          onHostAction={onHostAction}
          onRules={() => {
            setShowMenu(false);
            setShowRules(true);
          }}
          onChat={() => {
            setShowMenu(false);
            setShowChat(true);
          }}
          onLeave={onLeave}
          onWear={onWear}
          onDress={() => {
            setShowMenu(false);
            setShowDress(true);
          }}
          onClose={() => setShowMenu(false)}
        />
      ) : null}

      {showDress ? (
        <WardrobeSheet onChanged={onLookAgain} onClose={() => setShowDress(false)} />
      ) : null}

      {showRules ? (
        <Rules
          gameName={gameName}
          objective={objective}
          howToPlay={howToPlay}
          onClose={() => setShowRules(false)}
        />
      ) : null}
    </div>
  );
}

function TurnPill({ view }: { view: MatchView }) {
  if (view.gameOver) return <span className="pill">Finished</span>;
  if (!view.turn) return <span className="pill">—</span>;

  const mine = view.seat !== null && view.activeSeats.indexOf(view.seat) >= 0;

  /*
   * "Everyone choosing" only when everyone really is.
   *
   * Several seats being able to act is not the same as everybody doing the same
   * thing. In Cheat every seat but one can call a bluff at any moment, so all of
   * them are active — but exactly one player is choosing what to put down, and
   * the table should say so. The server tells us which case this is, because it
   * is the only side that can: the engine's default stage is `null`, so the two
   * are indistinguishable from the stage alone.
   */
  const many = view.activeSeats.length > 1 && view.turn.shared === true;

  /*
   * Everybody at once has nowhere else to be said.
   *
   * In Cheat every seat but one may call a bluff, so there is no single avatar
   * to point at - several are active and only one holds the clock. This is the
   * one turn state the table cannot express by lighting up a face.
   */
  if (many) return <span className={`pill${mine ? ' mine' : ''}`}>Everyone choosing</span>;

  /*
   * One named seat needs nothing here.
   *
   * Somebody else's turn is already drawn where it is happening - their avatar
   * carries the sweep and the seconds - and your own is drawn by the screen
   * pulsing gold at its edge, with the clock beside your own action where "how
   * long have I got" sits next to "what do I do". Repeating either in the
   * corner was the duplication that made the turn hard to locate rather than
   * easy: two places saying it, neither of them where the turn was.
   */
  return null;
}
